import { booleanAttribute, ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Params, RouterLink } from '@angular/router';

/** The "← All users" link above a record page's header. */
export interface PageBackLink {
  label: string;
  link: unknown[];
  queryParams?: Params;
}

/**
 * The hero card that opens each admin list page — the same card as the
 * greeting on cnt-landing's host and guest dashboards
 * (host-dashboard.component.html): a white-to-cream gradient with a gold
 * glow top right and a green glow bottom left, an orange eyebrow, a large
 * headline, a muted meta line and pill actions on the right.
 *
 * Actions are projected: `<ion-button actions>New POI</ion-button>`.
 *
 * Record pages (a user, a reservation, an editor) pass `back` and `compact`,
 * and can project status chips (`[status]`) and an avatar or photo (`[lead]`).
 */
@Component({
  selector: 'admin-page-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  template: `
    @if (back) {
      <a class="back" [routerLink]="back.link" [queryParams]="back.queryParams">← {{ back.label }}</a>
    }
    <header class="hero" [class.compact]="compact">
      <div class="lead"><ng-content select="[lead]"></ng-content></div>
      <div class="text">
        @if (eyebrow) {
          <span class="eyebrow">{{ eyebrow }}</span>
        }
        <div class="title-row">
          <h1>{{ title }}</h1>
          <div class="status"><ng-content select="[status]"></ng-content></div>
        </div>
        @if (meta) {
          <p class="meta">{{ meta }}</p>
        }
      </div>
      <div class="actions">
        <ng-content select="[actions]"></ng-content>
      </div>
    </header>
  `,
  styles: [
    `
      :host {
        display: block;
        margin-bottom: 32px;
      }
      .back {
        display: inline-flex;
        align-items: center;
        margin: 0 0 10px 4px;
        color: var(--cnt-muted, #707070);
        font-family: var(--cnt-font-button, sans-serif);
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .back:hover {
        color: var(--cnt-trinidad, #e3530d);
      }
      .lead:empty,
      .status:empty {
        display: none;
      }
      .lead {
        flex: none;
      }
      .title-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 12px;
      }
      .status {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      .hero.compact {
        padding: 22px 24px;
        border-radius: 1.25rem;
      }
      .hero.compact h1 {
        font-size: clamp(1.6rem, 2.6vw, 2.25rem);
      }
      @media (min-width: 768px) {
        .hero.compact {
          align-items: center;
          padding: 26px 32px;
        }
      }
      .hero {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 28px 24px;
        overflow: hidden;
        border: 1px solid var(--cnt-line, rgba(34, 34, 34, 0.08));
        border-radius: 1.5rem;
        background: radial-gradient(ellipse at 95% 0%, rgba(251, 215, 132, 0.35) 0%, transparent 55%),
          radial-gradient(ellipse at 5% 100%, rgba(41, 93, 66, 0.18) 0%, transparent 50%),
          linear-gradient(135deg, #ffffff 0%, #f7f5ec 100%);
      }
      @media (min-width: 768px) {
        .hero {
          flex-direction: row;
          align-items: flex-end;
          justify-content: space-between;
          gap: 24px;
          padding: 36px 40px;
        }
      }
      .text {
        flex: 1;
        min-width: 0;
      }
      .eyebrow {
        display: block;
        margin-bottom: 8px;
        color: var(--cnt-trinidad, #e3530d);
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      /* Focused after each navigation for screen readers; not a control, so no ring. */
      h1:focus {
        outline: none;
      }
      h1 {
        margin: 0;
        color: var(--cnt-ink, #222);
        font-size: clamp(2rem, 3.6vw, 3rem);
        font-weight: 700;
        line-height: 1.05;
        letter-spacing: -0.02em;
      }
      .meta {
        margin: 12px 0 0;
        color: var(--cnt-muted, #707070);
        font-size: 0.8125rem;
        line-height: 1.4;
      }
      .actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
      }
      .actions:empty {
        display: none;
      }
    `,
  ],
})
export class AdminPageHeaderComponent {
  /** Small orange label above the title, e.g. the menu group ("Marketplace"). */
  @Input() eyebrow = '';
  @Input({ required: true }) title = '';
  /** Muted line under the title, e.g. "1,204 users". */
  @Input() meta = '';
  /** A record page's way back up, e.g. { label: 'All users', link: ['/users'] }. */
  @Input() back?: PageBackLink;
  /** Tighter card and smaller title, for record pages. */
  @Input({ transform: booleanAttribute }) compact = false;
}
